import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../components/collapsibleSection";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { IEventIdsItem, IHomepageDetailData, IPostData } from "interfaces";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import Select from "components/select";
import { useState } from "react";

interface IEventField extends IPostData {
  id: string;
}

const EditHotEventSchema = z.object({
  id: z.string().min(1, "This field is required"),
});

const EditHotEventsSchema = z.object({
  data: z.array(EditHotEventSchema).max(9, {
    message: `You can add at most ${9} events`,
  }),
});

export const HotEventsForm = ({
  defaultData,
}: {
  defaultData: IHomepageDetailData["home"]["hot-event"];
}) => {
  const navigate = useNavigate();
  const [trigger, setTrigger] = useState(false);

  const { data: eventsData, isLoading: isLoadingEvents } = useOne<
    IEventIdsItem[]
  >({
    resource: "content/dropdown/events?v=2",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!trigger,
    } as any,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ data: IPostData[] }, HttpError, { data: IPostData[] }>({
    defaultValues: {
      data: defaultData.data,
    },
    resolver: zodResolver(EditHotEventsSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: { data: IPostData[] }) => {
    const submitValues = values.data.map((value) => `${value.id}`);
    mutate(
      {
        resource: `content/homepage`,
        id: `${defaultData.id}/hot-event/home/update`,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          id: [...submitValues],
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/mkt/homepage/detail?default=event`);
        },
      }
    );
  };

  const events = (eventsData?.data ?? []).map((event: IEventIdsItem) => ({ value: event.id, label: event.name_en }));

  return (
    <CollapsibleSection
      label="Hot Events (Max 9 events)"
      defaultOpen={false}
      onClick={() => !trigger && setTrigger(true)}
    >
      <form
        className="divide-y divide-gray-100"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full px-4 py-6 sm:px-6">
          <div className="w-full pb-6">
            {fields.length < 9 ? (
              <Button
                type="button"
                className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={() => {
                  append({ position: 0, name: "", id: "" });
                }}
              >
                <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                <span className="shrink-0 text-sm">Add more</span>
              </Button>
            ) : (
              <></>
            )}
          </div>
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 py-4">
            {fields.map((field: IEventField, index: number) => (
              <div className="sm:col-span-2" key={field.id}>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label
                      id={`event_${field.id}`}
                      name={`Event Name ${index + 1}`}
                      required
                    />
                    <button onClick={() => remove(index)}>
                      <TrashIcon className="w-5 h-5 text-brand-500" />
                    </button>
                  </div>
                  <Controller
                    control={control}
                    name={`data.${index}.id`}
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <Select
                          key={`event-id__${field.id}`}
                          inputRef={ref}
                          options={events}
                          id="organization_id"
                          placeholder="Select Organization"
                          defaultValue={
                            events.find(
                              (selected: { label: string; value: string }) =>
                                selected.value === value
                            ) ??
                            (field.name && field.id)
                              ? {
                                  label: field.name,
                                  value: field.id,
                                }
                              : ""
                          }
                          value={events.find(
                            (selected: { label: string; value: string }) =>
                              selected.value === value
                          )}
                          onChange={(selected: {
                            label: string;
                            value: string;
                          }) => {
                            onChange(selected?.value);
                          }}
                          error={get(errors.data, `${index}.id`, "")}
                          isLoading={isLoadingEvents}
                        />
                      );
                    }}
                  />
                  {get(errors.data, `${index}.id`, "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors.data, `${index}.id.message`, "")}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </CollapsibleSection>
  );
};
